import { IconChevronDown } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { ISideNavItem, ISideNavSubItem } from '../../../interfaces';
import {
    hasActiveChild,
    hasSubgroupHeaders,
    isItemActive,
    isPathActive,
    isSideNavSubGroupHeader,
    isSideNavSubLink,
    sortChildrenAlpha,
} from '../../../helpers';
import useSideNavCollapsed from '../../../context/SideNavCollapsedContext';
import SideNavTooltip from './SideNavTooltip/SideNavTooltip';
import './SideNavItem.scss';

const BLOCK = 'SideNavItem';
const CHILD_BLOCK = `${BLOCK}__child`;

const getRowClassName = (isActive: boolean, isCollapsed: boolean) => classNames(
    BLOCK,
    'cursor--pointer',
    isActive ? 'text-dark' : 'text-muted',
    isActive && `${BLOCK}--active`,
    isCollapsed && `${BLOCK}--collapsed justify-content-center`,
);

const getChildClassName = (isActive: boolean) => classNames(
    CHILD_BLOCK,
    'cursor--pointer w-100',
    isActive ? 'text-dark' : 'text-muted',
    isActive && `${CHILD_BLOCK}--active`,
);

interface IProps {
    item: ISideNavItem;
    isCollapsed: boolean;
    onCollapsedParentClick: (parentId: string) => void;
    openParentIds: Set<string>;
    onOpenParent: (parentId: string) => void;
    onToggleParent: (parentId: string) => void;
}

const renderSubItem = (child: ISideNavSubItem, pathname: string) => {
    const isActive = isPathActive(pathname, child.to);
    const childClassName = getChildClassName(isActive);

    const content = (
        <>
            <span className={`${CHILD_BLOCK}__bullet`} aria-hidden="true" />
            <span className={`${CHILD_BLOCK}__label`}>
                <span className={`${CHILD_BLOCK}__label-text`}>{child.label}</span>
            </span>
        </>
    );

    if (child.useAnchorTag) {
        return (
            <a key={child.id} href={child.to} className={childClassName}>
                {content}
            </a>
        );
    }

    return (
        <Link key={child.id} to={child.to} className={childClassName}>
            {content}
        </Link>
    );
};

const SideNavItem = ({
    item,
    isCollapsed,
    onCollapsedParentClick,
    openParentIds,
    onOpenParent,
    onToggleParent,
}: IProps) => {
    const { pathname } = useLocation();
    const { setCollapsed } = useSideNavCollapsed();
    const reactId = useId();
    const targetId = `side-nav-v2-item-${item.id}-${reactId.replace(/:/g, '')}`;
    const isActive = isItemActive(item, pathname);
    const itemHasActiveChild = hasActiveChild(item, pathname);
    const [autoOpened, setAutoOpened] = useState(false);
    useEffect(() => {
        if (item.kind === 'hasChildren' && itemHasActiveChild && !openParentIds.has(item.id) && !autoOpened) {
            onOpenParent(item.id);
            setAutoOpened(true);
        }
    }, [itemHasActiveChild, item.id, item.kind, openParentIds, onOpenParent, autoOpened]);

    const isOpen = item.kind === 'hasChildren' && openParentIds.has(item.id);
    const showTooltip = isCollapsed;
    const testId = item.testId ?? `sideNavV2Item-${item.id}`;
    const rowClassName = getRowClassName(isActive, isCollapsed);

    const iconAndLabel = (
        <>
            <span className={`${BLOCK}__icon`}>
                <NSIcon icon={item.icon} />
            </span>
            {!isCollapsed && (
                <span className={`${BLOCK}__label`}>
                    <span className={`${BLOCK}__label-text`}>{item.label}</span>
                </span>
            )}
        </>
    );

    if (item.kind === 'standard') {
        const handleClick = () => {
            if (isCollapsed) {
                setCollapsed(false);
            }
        };

        const sharedProps = {
            id: targetId,
            className: rowClassName,
            'data-testid': testId,
            'aria-label': item.label,
            onClick: handleClick,
        };

        return (
            <li>
                {item.useAnchorTag ? (
                    <a href={item.to ?? '#'} {...sharedProps}>
                        {iconAndLabel}
                    </a>
                ) : (
                    <Link to={item.to ?? '#'} {...sharedProps}>
                        {iconAndLabel}
                    </Link>
                )}
                <SideNavTooltip targetId={targetId} label={item.label} enabled={showTooltip} />
            </li>
        );
    }

    const children = item.children ?? [];
    let displayChildren = children;
    if (!hasSubgroupHeaders(children)) {
        const subLinks = children.filter(isSideNavSubLink);
        displayChildren = item.preserveChildOrder ? subLinks : sortChildrenAlpha(subLinks);
    }

    const childGroups: { header?: string; items: ISideNavSubItem[] }[] = [];
    displayChildren.forEach(entry => {
        if (isSideNavSubGroupHeader(entry)) {
            childGroups.push({ header: entry.label, items: [] });
        } else {
            if (!childGroups.length) childGroups.push({ items: [] });
            childGroups[childGroups.length - 1].items.push(entry);
        }
    });

    const toggleParent = () => (isCollapsed ? onCollapsedParentClick(item.id) : onToggleParent(item.id));

    return (
        <div>
            <div
                id={targetId}
                role="button"
                tabIndex={0}
                className={getRowClassName(isCollapsed && itemHasActiveChild, isCollapsed)}
                data-testid={testId}
                aria-expanded={isOpen}
                aria-label={item.label}
                onClick={toggleParent}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleParent();
                    }
                }}
            >
                {iconAndLabel}
                {!isCollapsed && (
                    <span className={classNames(`${BLOCK}__chevron`, 'ml-auto mr-2', isOpen && `${BLOCK}__chevron--open`)}>
                        <NSIcon icon={IconChevronDown} />
                    </span>
                )}
            </div>

            <SideNavTooltip targetId={targetId} label={item.label} enabled={showTooltip} />

            <RenderIf isTrue={!isCollapsed && isOpen}>
                <div className="text-muted pb-1">
                    {childGroups.map((group, idx) => (
                        <React.Fragment key={group.header ?? idx}>
                            {group.header && (
                                <div className={`${BLOCK}__group-header text-uppercase font-10 text-muted`}>
                                    {group.header}
                                </div>
                            )}
                            <div>
                                {group.items.map(child => renderSubItem(child, pathname))}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </RenderIf>
        </div>
    );
};

export default SideNavItem;
